import { Observable } from 'rxjs';

/** The wallpaper as the sharp `<img>` and its blurred CSS copies use it. */
export interface Wallpaper {
  /** For `<img class="app-background" crossorigin="anonymous">`. */
  src: string;
  /** For `--app-background-image`; `null` when no same-origin copy could be made. */
  css: string | null;
}

/**
 * Loads a wallpaper once in CORS mode and hands out a same-origin `blob:` URL.
 *
 * The app is cross-origin isolated (COEP `require-corp`). The statically
 * blurred copies (static-frost in styles/mixins.scss) load the wallpaper
 * through CSS, which can't use CORS, so a cross-origin image served without
 * `Cross-Origin-Resource-Policy` — S3 sends none — is blocked. Chromium then
 * answers the `<img crossorigin>` for the same URL from that cached no-cors
 * response, which has no `Access-Control-Allow-Origin`, so the sharp wallpaper
 * fails as well. A `blob:` URL is same-origin for both.
 *
 * A CORS failure is retried once past the HTTP cache, which may still hold
 * such a no-cors response. If the image can't be fetched at all, the `<img>`
 * keeps the original URL and the copies show only their tint. The blob URL is
 * revoked when the subscription ends.
 */
export function wallpaper$(url: string): Observable<Wallpaper> {
  return new Observable<Wallpaper>((subscriber) => {
    const abort = new AbortController();
    let objectUrl: string | undefined;
    const load = (cache: RequestCache) =>
      fetch(url, { mode: 'cors', credentials: 'omit', cache, signal: abort.signal });
    load('default')
      .catch((error: unknown) => {
        if (abort.signal.aborted) throw error;
        return load('reload');
      })
      .then((response) => {
        if (!response.ok) throw new Error(`Wallpaper request failed: ${response.status}`);
        return response.blob();
      })
      .then((blob) => {
        if (abort.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        subscriber.next({ src: objectUrl, css: `url(${JSON.stringify(objectUrl)})` });
      })
      .catch(() => {
        if (!abort.signal.aborted) subscriber.next({ src: url, css: null });
      });
    return () => {
      abort.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  });
}
